import { toast } from 'react-toastify';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  call,
  put,
  SagaReturnType,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';

import * as api from 'src/api/api';
import { UserTransactions, UserType } from 'src/types/admin';
import { Organization, User } from 'src/types/auth';
import { Collection } from 'src/types/collection';
import { ServerError } from 'src/types/core';
import {
  Community,
  CommunityMember,
  CommunityMembersResult,
} from 'src/types/forum';
import { CatalogsEntities } from 'src/types/insights';
import {
  getDefaultCommunity,
  processAddCommunityMember,
  processAddUser,
  processCatalogRoleUpdates,
  processCollectionRoleUpdates,
  processCommunityRoleUpdates,
  processLinkUser,
  processOrganizationRoleUpdates,
  processRemoveCommunityMember,
  processRemoveUser,
  processUserUpdates,
} from './admin-slice-helpers';

interface AdminState {
  transactions: UserTransactions[];
  users: User[];
  currentUserId: number | undefined;
  userFetchError: ServerError | undefined;
  userType: UserType | undefined;
  organization: Organization | undefined;
  collections: Collection[];
  collectionsFetchError: ServerError | undefined;
  communities: Community[];
  community: Community | undefined;
  communitiesFetchError: ServerError | undefined;
  communityMembers: CommunityMembersResult;
  communityMembersFetchError: ServerError | undefined;
  organizationChangeFetchError: ServerError | undefined;
  usersLoading: boolean;
  editCommunityDetail: {
    isUpdating: boolean;
    error: ServerError | undefined;
    communityId: number | undefined;
  };
  catalogs: CatalogsEntities['catalogs'];
  catalogsLoading: boolean;
  catalogsFetchError: ServerError | undefined;
}

// initial state for reducer
const initialState: AdminState = {
  transactions: [],
  users: [],
  currentUserId: undefined,
  userFetchError: undefined,
  userType: undefined,
  organization: undefined,
  collections: [],
  collectionsFetchError: undefined,
  communities: [],
  community: undefined,
  communitiesFetchError: undefined,
  communityMembers: {
    community: undefined,
    non_community_users: [],
    community_users: [],
  },
  communityMembersFetchError: undefined,
  organizationChangeFetchError: undefined,
  usersLoading: false,
  editCommunityDetail: {
    isUpdating: false,
    error: undefined,
    communityId: undefined,
  },
  catalogs: {},
  catalogsLoading: false,
  catalogsFetchError: undefined,
};

const slice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    setOrganization(state, action: PayloadAction<Organization>) {
      state.organization = action.payload;
      state.communityMembers = initialState.communityMembers;
      state.usersLoading = true;
    },
    getOrganizationChangeFailure(state, action: PayloadAction<ServerError>) {
      state.organizationChangeFetchError = action.payload;
      state.usersLoading = false;
    },
    setCommunity(state, action: PayloadAction<Community>) {
      state.community = action.payload;
      state.usersLoading = true;
    },
    getCollectionsSuccess(state, action: PayloadAction<Collection[]>) {
      state.collections = action.payload;
      state.usersLoading = false;
    },
    getCollectionsFailure(state, action: PayloadAction<ServerError>) {
      state.collectionsFetchError = action.payload;
    },
    getCommunitiesSuccess(state, action: PayloadAction<Community[]>) {
      state.communities = action.payload;
      state.community = action.payload[0];
      state.usersLoading = false;
    },
    getCommunityMembersSuccess(
      state,
      action: PayloadAction<CommunityMembersResult>
    ) {
      state.communityMembers = action.payload;
      state.usersLoading = false;
    },
    getCommunityMembersFailure(state, action: PayloadAction<ServerError>) {
      state.communityMembersFetchError = action.payload;
    },
    updateCommunityDetails(
      state,
      action: PayloadAction<{
        communityId: Community['id'];
        changes: Partial<Community>;
      }>
    ) {
      state.editCommunityDetail.isUpdating = true;
      state.editCommunityDetail.error = undefined;
      state.editCommunityDetail.communityId = action.payload.communityId;
    },
    updateCommunityDetailsSuccess(state, action: PayloadAction<Community>) {
      state.editCommunityDetail.isUpdating = false;
      state.editCommunityDetail.error = undefined;
      state.community = action.payload;
      state.editCommunityDetail.communityId = action.payload.id;
      if (state.communities.length) {
        state.communities.forEach((com, i) => {
          if (com.id === action.payload.id) {
            state.communities[i] = action.payload;
          }
        });
      }
    },
    updateCommunityDetailsFailure(state, action: PayloadAction<ServerError>) {
      state.editCommunityDetail.isUpdating = false;
      state.editCommunityDetail.error = action.payload;
    },
    getUsers(
      state,
      action: PayloadAction<{ organizationId?: number; userType: UserType }>
    ) {
      state.userType = action.payload.userType;
      state.usersLoading = true;
    },
    getUsersSuccess(state, action: PayloadAction<User[]>) {
      state.users = action.payload;
      state.usersLoading = false;
    },
    getUsersFailure(state, action: PayloadAction<ServerError>) {
      state.userFetchError = action.payload;
      state.usersLoading = false;
    },
    setCurrentUser(state, action: PayloadAction<{ id: number | undefined }>) {
      state.currentUserId = action.payload.id;
    },
    userTransaction: {
      reducer(state, action: PayloadAction<UserTransactions>) {
        // Add transaction to list of transactions
        state.transactions = state.transactions.concat([action.payload]);
      },
      prepare: (userTransaction: UserTransactions) => {
        return { payload: userTransaction };
      },
    },
    userTransactionSuccess(
      state,
      action: PayloadAction<{
        updatedUser: Partial<User>;
        transaction: UserTransactions;
      }>
    ) {
      const actionType = action.payload.transaction.type;

      // NOTE: this works well BUT getting all community members
      // gives some members without their avatar url while
      // getting org members gets it so the avatar treatement
      // in a table mixed with user data from both has some
      // with the initials and some with user images
      if (actionType === 'add_community_member') {
        state.communityMembers.community_users = [
          ...state.communityMembers.community_users,
          action.payload.updatedUser as CommunityMember,
        ];
        state.communityMembers.non_community_users =
          state.communityMembers.non_community_users.filter(
            (u) => u.id !== action.payload.updatedUser.id
          );
      } else if (actionType === 'remove_community_member') {
        state.communityMembers.non_community_users = [
          ...state.communityMembers.non_community_users,
          action.payload.updatedUser as CommunityMember,
        ];
        state.communityMembers.community_users =
          state.communityMembers.community_users.filter(
            (u) => u.id !== action.payload.updatedUser.id
          );
      }

      let newUsers = [...state.users];
      if (actionType === 'remove') {
        newUsers = newUsers.filter(
          (user) => user.id !== action.payload.updatedUser.id
        );
      } else if (actionType === 'create') {
        const existingUser = state.users.filter(
          (user: User) => user.id === action.payload.updatedUser.id
        )[0];
        if (!existingUser) {
          // Prevent duiplicates
          newUsers.push(action.payload.updatedUser as User);
        }
        // Must modify transaction to contain the userId after post request
        const newTransactions = state.transactions.map((transaction) => {
          if (transaction.id === action.payload.transaction.id) {
            const transactionData = {
              ...transaction.data,
              id: action.payload.updatedUser.id as number,
            };
            transaction.data = transactionData;
          }
          return transaction;
        });
        state.transactions = newTransactions;
      } else {
        newUsers = state.users.map((user) => {
          if (user.id === action.payload.updatedUser.id) {
            return Object.assign(user, action.payload.updatedUser);
          }
          return user;
        });
      }
      state.users = newUsers;

      if (action.payload.transaction.toast?.onSuccess) {
        const toastConfig = action.payload.transaction.toast.onSuccess;
        toast.success(toastConfig.content, toastConfig.options);
      }
    },
    removeUserTransaction(state, action: PayloadAction<UserTransactions>) {
      const newTransactions = state.transactions.filter((transaction) => {
        return transaction.id !== action.payload.id;
      });
      state.transactions = newTransactions;
    },
    userTransactionFailure(
      state,
      action: PayloadAction<{
        error: ServerError;
        transaction: UserTransactions;
      }>
    ) {
      const newTransactions = state.transactions.map((transaction) => {
        if (transaction.id === action.payload.transaction.id) {
          transaction.error = action.payload.error;
        }
        return transaction;
      });
      state.transactions = newTransactions;

      if (action.payload.transaction.toast?.onError) {
        const toastConfig = action.payload.transaction.toast.onError;
        toast.error(
          toastConfig.content(action.payload.error),
          toastConfig.options
        );
      }
    },
    loadCatalogs(state) {
      state.catalogsLoading = true;
    },
    loadCatalogsSuccess(state, action: PayloadAction<CatalogsEntities>) {
      state.catalogsLoading = false;
      state.catalogs = action.payload.catalogs || {};
      state.catalogsFetchError = undefined;
    },
    loadCatalogsFailure(state, action: PayloadAction<ServerError>) {
      state.catalogsLoading = false;
      state.catalogsFetchError = action.payload;
      toast.error('Failed to load catalogs.', {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    },
  },
});

export const {
  getUsers,
  getUsersSuccess,
  getUsersFailure,
  userTransaction,
  userTransactionSuccess,
  userTransactionFailure,
  removeUserTransaction,
  setOrganization,
  setCommunity,
  getCollectionsSuccess,
  getCollectionsFailure,
  getCommunitiesSuccess,
  getCommunityMembersSuccess,
  getCommunityMembersFailure,
  updateCommunityDetails,
  updateCommunityDetailsSuccess,
  updateCommunityDetailsFailure,
  getOrganizationChangeFailure,
  setCurrentUser,
  loadCatalogs,
  loadCatalogsFailure,
  loadCatalogsSuccess,
} = slice.actions;
export const actions = slice.actions;

export default slice.reducer;

export function* sagaGetUsers(action: ReturnType<typeof getUsers>) {
  try {
    const { userType, organizationId } = action.payload;
    let userList = [];
    if (userType === 'members' && organizationId != null) {
      userList = yield call(api.getUsersInOrganization, organizationId);
    }
    // fire the action with successful response
    yield put(getUsersSuccess(userList));
  } catch (err) {
    // an error occurred, fire the failure action
    yield put(getUsersFailure(err as ServerError));
  }
}

export function* sagaSetOrganization(
  action: ReturnType<typeof setOrganization>
) {
  try {
    const organization = action.payload;

    // Get the organization users
    const userList: SagaReturnType<typeof api.getUsersInOrganization> =
      yield call(api.getUsersInOrganization, organization.id);

    // Get the collections in the organization
    const collections: SagaReturnType<typeof api.getCollections> = yield call(
      api.getCollections,
      {
        organizationId: organization.id,
      }
    );

    // Get the communities in the organization
    const communities: SagaReturnType<typeof api.getCommunities> = yield call(
      api.getCommunities,
      {
        organizationId: organization.id,
      }
    );

    // Set the default community
    const defaultCommunity: SagaReturnType<typeof getDefaultCommunity> =
      yield call(getDefaultCommunity, communities);

    // Put the successful data in the state object
    yield put(getCollectionsSuccess(collections));
    yield put(getUsersSuccess(userList));
    yield put(getCommunitiesSuccess(communities));

    // Do this last as `setCommunity` triggers get sagaGetCommunityMembers
    // and all of these actions are using the same loading flag
    if (defaultCommunity) {
      yield put(setCommunity(defaultCommunity));
    }
  } catch (err) {
    // an error occurred, fire the failure action
    yield put(getOrganizationChangeFailure(err as ServerError));
  }
}

export function* sagaGetCommunityMembers(
  action: ReturnType<typeof setCommunity>
) {
  try {
    const community = action.payload;
    const communityMembers: SagaReturnType<typeof api.getCommunityMembers> =
      yield call(api.getCommunityMembers, {
        communityId: community.id,
      });
    // fire the action with successful response
    yield put(getCommunityMembersSuccess(communityMembers));
  } catch (err) {
    // an error occurred, fire the failure action
    yield put(getCommunityMembersFailure(err as ServerError));
  }
}

export function* sagaEditCommunityDetails(
  action: ReturnType<typeof updateCommunityDetails>
) {
  try {
    const { communityId, changes } = action.payload;
    const updatedCommunity: SagaReturnType<typeof api.updateCommunityDetails> =
      yield call(api.updateCommunityDetails, communityId, changes);
    yield put(updateCommunityDetailsSuccess(updatedCommunity));
  } catch (err) {
    yield put(updateCommunityDetailsFailure(err as ServerError));
  }
}

export function* sagaUserTransaction(
  action: ReturnType<typeof userTransaction>
) {
  try {
    switch (action.payload.type) {
      case 'create':
        try {
          yield call(processAddUser, action.payload);
        } catch (err) {
          const failure = JSON.parse((err as Error).message);

          if (failure.data.id) {
            // The user already exists
            yield call(processLinkUser, action.payload, failure.data.id);
          } else {
            throw new Error(err as string);
          }
        }
        break;
      case 'update_user_details':
        yield call(processUserUpdates, action.payload);
        break;
      case 'update_user_organization_role':
        yield call(processOrganizationRoleUpdates, action.payload);
        break;
      case 'update_user_community_role':
        yield call(processCommunityRoleUpdates, action.payload);
        break;
      case 'update_user_collection_role':
        yield call(processCollectionRoleUpdates, action.payload);
        break;
      case 'update_user_catalog_role':
        yield call(processCatalogRoleUpdates, action.payload);
        break;
      case 'remove':
        yield call(processRemoveUser, action.payload);
        break;
      case 'remove_community_member':
        yield call(processRemoveCommunityMember, action.payload);
        break;
      case 'add_community_member':
        yield call(processAddCommunityMember, action.payload);
        break;
    }
  } catch (err) {
    const error = api.transformAccountsServerError(err as Error);
    // an error occurred, fire the failure action
    yield put(userTransactionFailure({ error, transaction: action.payload }));
  }
}

export function* sagaRemoveUserTransaction(
  action: ReturnType<typeof userTransactionSuccess>
) {
  yield put(removeUserTransaction(action.payload.transaction));
}

export function* sagaLoadCatalogs(action: ReturnType<typeof loadCatalogs>) {
  try {
    const catalogs: SagaReturnType<typeof api.getCatalogs> = yield call(
      api.getCatalogs,
      true
    );
    yield put(loadCatalogsSuccess(catalogs));
  } catch (err) {
    yield put(loadCatalogsFailure(err as ServerError));
  }
}

export const sagas = [
  takeEvery(setOrganization.type, sagaSetOrganization),
  takeEvery(getUsers.type, sagaGetUsers),
  takeEvery(setCommunity.type, sagaGetCommunityMembers),
  takeEvery(updateCommunityDetails.type, sagaEditCommunityDetails),
  takeEvery(userTransaction.type, sagaUserTransaction),
  takeEvery(userTransactionSuccess.type, sagaRemoveUserTransaction),
  takeLatest(loadCatalogs.type, sagaLoadCatalogs),
];
